import { Link } from 'react-router-dom';
import useScrollAnimation from '../../hooks/useScrollAnimation';

interface ServiceCard {
  image: string;
  title: string;
  description: string;
  href: string;
}

interface ServicesOverviewProps {
  eyebrow?: string;
  headline: string;
  subheadline?: string;
  services: ServiceCard[];
  ctaLabel?: string;
  ctaHref?: string;
}

const HEIGHTS = [
  'min-h-[520px]', 'min-h-[400px]', 'min-h-[460px]',
  'min-h-[400px]', 'min-h-[520px]', 'min-h-[380px]',
];
const OFFSETS = [
  '', 'lg:mt-12', 'lg:mt-6',
  'lg:mt-10', '', 'lg:mt-14',
];

const ServicesOverview = ({
  eyebrow = 'What We Do',
  headline,
  subheadline,
  services,
  ctaLabel = 'View All Services',
  ctaHref = '/services',
}: ServicesOverviewProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-[#f7f4ef] py-24 lg:py-32 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div ref={ref} className={`anim-section ${isVisible ? 'is-visible' : ''} mb-16`}>
          {eyebrow && (
            <p className="text-[#c8a951] text-xs uppercase tracking-[0.35em] font-semibold mb-4">{eyebrow}</p>
          )}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-display text-4xl lg:text-5xl font-bold text-[#0a0a0a] max-w-xl leading-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {headline}
            </h2>
            {subheadline && (
              <p className="text-[#0a0a0a]/50 text-base leading-relaxed max-w-sm lg:text-right">{subheadline}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
          {services.map((svc, i) => (
            <ServiceCardItem
              key={i}
              svc={svc}
              index={i}
              height={HEIGHTS[i] ?? 'min-h-[420px]'}
              offset={OFFSETS[i] ?? ''}
            />
          ))}
        </div>

        {ctaHref && (
          <div className="mt-14 text-center">
            <Link
              to={ctaHref}
              className="inline-block border border-[#0a0a0a] text-[#0a0a0a] text-xs font-bold px-12 py-4 uppercase tracking-[0.18em] hover:bg-[#0a0a0a] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {ctaLabel}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

const ServiceCardItem = ({
  svc,
  index,
  height,
  offset,
}: {
  svc: ServiceCard;
  index: number;
  height: string;
  offset: string;
}) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`anim-section ${isVisible ? 'is-visible' : ''} ${offset} relative overflow-hidden group cursor-pointer`}
      style={{ transitionDelay: `${(index % 3) * 0.12}s` }}
    >
      <Link to={svc.href} className="block">
        <div className={`relative ${height} overflow-hidden`}>
          <img
            src={svc.image}
            alt={svc.title}
            className="w-full h-full object-cover object-top absolute inset-0 group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/5 group-hover:via-black/50 transition-all duration-500" />
          <div className="absolute inset-0 border border-transparent group-hover:border-[#c8a951]/60 transition-colors duration-500" />

          {/* Number badge */}
          <div className="absolute top-6 left-6">
            <span className="text-[#c8a951]/60 text-xs font-bold tracking-[0.3em]">
              {String(index + 1).padStart(2, '0')}
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-7">
            <h3
              className="font-display text-xl font-bold text-white mb-2 leading-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {svc.title}
            </h3>
            <p className="text-white/60 text-sm leading-relaxed mb-4">{svc.description}</p>
            <span className="inline-flex items-center gap-2 text-[#c8a951] text-xs uppercase tracking-[0.2em] font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
              Learn More <i className="ri-arrow-right-line" />
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ServicesOverview;
